import { Chip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Box } from "@mui/system";
import { BigTooltip } from "components/BigTooltip";
import { stateWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import { StateRegulatoryStatus } from "generated-graphql/graphql";
import { ReactNode } from "react";

const SameAsFederalTooltips: Partial<Record<StateRegulatoryStatus, ReactNode>> = {
  [StateRegulatoryStatus.VerySmallQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Very Small Quantity Generator (VSQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated 220 lbs or less of hazardous waste per month [or combo of hazardous + state
        regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
        per month
      </Typography>
    </Box>
  ),
  [StateRegulatoryStatus.SmallQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Small Quantity Generator (SQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated more than 220 lbs and less than 2,200 lbs of hazardous waste per month [or combo
        of hazardous + state regulated + acutely hazardous] and generated less than 2.2 lbs of
        acutely hazardous waste per month
      </Typography>
    </Box>
  ),
  [StateRegulatoryStatus.LargeQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Large Quantity Generator (LQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated more than 2,200 lbs of hazardous waste per month [or combo of hazardous + state
        regulated + acutely hazardous] or generated more than 2.2 lbs of acutely hazardous waste per
        month
      </Typography>
    </Box>
  ),
};

const StateGeneratorStatusTooltips: Record<
  string,
  Partial<Record<StateRegulatoryStatus, ReactNode>>
> = {
  CA: {
    [StateRegulatoryStatus.SmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          California Small Quantity Generator (SQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated less than 2,200 lbs of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.LargeQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          California Large Quantity Generator (LQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 2,200 lbs of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] or generated more than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
  },
  KS: {
    [StateRegulatoryStatus.ConditionallyExemptSmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Conditionally Exempt Small Quantity Generator (CESQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated less than 55 lbs of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.KansasSmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Kansas Small Quantity Generator (KSQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 55 lbs and less than 220 lbs of hazardous waste per month [or combo of
          hazardous + state regulated + acutely hazardous] and generated less than 2.2 lbs of
          acutely hazardous waste per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.SmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.SmallQuantityGenerator],

    [StateRegulatoryStatus.LargeQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.LargeQuantityGenerator],
  },
  ME: {
    [StateRegulatoryStatus.SmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Maine Small Quantity Generator (SQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated 220 lbs or less of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.LargeQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Main Large Quantity Generator (LQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 220 lbs per month [or combo of hazardous + state regulated + acutely
          hazardous] or generated more than 2.2 lbs of acutely hazardous waste per month
        </Typography>
      </Box>
    ),
  },
  MD: {
    [StateRegulatoryStatus.SmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Maryland Small Quantity Generator (SQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated 220 lbs or less of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.MarylandGeneratorFullyRegulated]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Maryland Generator (Fully Regulated)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 220 lbs per month [or combo of hazardous + state regulated + acutely
          hazardous] or generated more than 2.2 lbs of acutely hazardous waste per month
        </Typography>
      </Box>
    ),
  },
  RI: {
    [StateRegulatoryStatus.ConditionallyExemptSmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Conditionally Exempt Small Quantity Generator (CESQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated 220 lbs or less of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.SmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.SmallQuantityGenerator],
    [StateRegulatoryStatus.LargeQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.LargeQuantityGenerator],
  },
  MN: {
    [StateRegulatoryStatus.MiniQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Minnesota Mini Quantity Generator (MiniQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated less than 100 lbs of hazardous waste per year and 0 lbs of acutely hazardous
          waste per year
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.VerySmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.VerySmallQuantityGenerator],
    [StateRegulatoryStatus.SmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.SmallQuantityGenerator],
    [StateRegulatoryStatus.LargeQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.LargeQuantityGenerator],
  },
  TX: {
    [StateRegulatoryStatus.TexasClass_1OnlyGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Class 1 Only Generator
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 220 lbs of Class 1 Industrial Waste per month and 0 lbs of hazardous
          waste per month [or combo of hazardous + state regulated + acutely hazardous]
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.VerySmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.VerySmallQuantityGenerator],
    [StateRegulatoryStatus.SmallQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.SmallQuantityGenerator],
    [StateRegulatoryStatus.LargeQuantityGenerator]:
      SameAsFederalTooltips[StateRegulatoryStatus.LargeQuantityGenerator],
  },
  WA: {
    [StateRegulatoryStatus.SmallQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Washington Small Quantity Generator (SQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated 220 lbs or less of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] and generated less than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.MediumQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Washington Medium Quantity Generator (MQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 220 lbs and less than 2,200 lbs of hazardous waste per month [or combo
          of hazardous + state regulated + acutely hazardous] and generated less than 2.2 lbs of
          acutely hazardous waste per month
        </Typography>
      </Box>
    ),
    [StateRegulatoryStatus.LargeQuantityGenerator]: (
      <Box>
        <Typography variant="caption" fontStyle="italic">
          Washington Large Quantity Generator (LQG)
        </Typography>
        <br />
        <Typography variant="caption">
          Generated more than 2,200 lbs of hazardous waste per month [or combo of hazardous + state
          regulated + acutely hazardous] or generated more than 2.2 lbs of acutely hazardous waste
          per month
        </Typography>
      </Box>
    ),
  },
};

export type StateGeneratorStatusIndicatorProps = {
  state: string;
  status: StateRegulatoryStatus | null | undefined;
};

export function StateGeneratorStatusIndicator({
  state,
  status,
}: StateGeneratorStatusIndicatorProps) {
  const tooltip = status
    ? (StateGeneratorStatusTooltips[state]?.[status] ?? SameAsFederalTooltips[status])
    : "";

  const label = status ? stateWasteGeneratorStatusToLabel(status) : "None";

  const childElement = (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      clickable={false}
      sx={{
        minWidth: "56px",
        height: "24px",
        border: `2px solid ${grey[400]}`,
        cursor: "pointer",
      }}
    />
  );

  return <BigTooltip title={tooltip}>{childElement}</BigTooltip>;
}
